import React, { useState } from "react";
import { Editor } from "@monaco-editor/react";
import api from "../api";
import { Code, Eye, Send, Smartphone, Monitor } from "lucide-react";

// Sample context used to render the preview iframe — same shape used by the
// per-list EmailTemplate page.
const SAMPLE_CTX = {
  SALUTO: "Ciao Mario",
  TITOLARE: "Mario Rossi",
  companyName: "Studio Dentistico Rossi S.r.l.",
  DOMINIO: "studiodentisticorossi.it",
  CITTA: "Lodi",
  PAGESPEED_MOBILE: "34",
  PAGESPEED_DESKTOP: "58",
  SEOZOOM_AUTHORITY: "12",
  SEOZOOM_TRAFFIC: "187",
  TOTAL_KEYWORDS: "64",
  analysisUrl: "https://sitidaincubo.maxvalle.app/r/abc123",
  calendarUrl: "https://calendar.app.google/jcUWkc51Wg3rN48CA",
  bookUrl: "https://maxvalle.it/libri",
};

function renderVars(html, ctx = SAMPLE_CTX) {
  if (!html) return "";
  let out = html;
  Object.entries(ctx).forEach(([k, v]) => {
    out = out.replace(new RegExp("{{\\s*" + k + "\\s*}}", "g"), v ?? "");
  });
  return out;
}

/**
 * Reusable HTML email template editor with live preview & test send.
 *
 * Props:
 *  - label         heading shown above the block
 *  - subtitle      optional helper text
 *  - subject       current subject string
 *  - html          current html string
 *  - onSubjectChange / onHtmlChange callbacks
 *  - height        Monaco/iframe height (default 380)
 *  - testId        prefix for data-testid attributes
 *  - subjectPlaceholder
 */
export default function EmailTemplateBlock({
  label,
  subtitle,
  subject,
  html,
  onSubjectChange,
  onHtmlChange,
  height = 380,
  testId = "tpl",
  subjectPlaceholder = "",
}) {
  const [preview, setPreview] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [testing, setTesting] = useState(false);

  const sendTest = async () => {
    if (!subject?.trim() || !html?.trim()) {
      alert("Compila oggetto e corpo prima di mandare il test.");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/email/test", { subject, html });
      if (data.success) alert("Email di test inviata!");
      else alert("Errore: " + (data.error || "sconosciuto"));
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setTesting(false); }
  };

  const previewHtml = renderVars(html);
  const previewSubject = renderVars(subject);
  const iframeWidth = mobile ? 360 : "100%";

  return (
    <div className="card mt-3" data-testid={`${testId}-block`}
      style={{padding:14, background:"var(--mv-bg-soft)"}}>
      <div className="flex justify-between items-center mb-3" style={{flexWrap:"wrap", gap:8}}>
        <div>
          <div style={{fontWeight:700, fontSize:14}}>{label}</div>
          {subtitle && <div className="text-small text-muted" style={{marginTop:2}}>{subtitle}</div>}
        </div>
        <div className="flex gap-2" style={{flexWrap:"wrap"}}>
          {preview && (
            <div style={{display:"inline-flex", border:"1px solid var(--mv-border)", borderRadius:6, overflow:"hidden"}}>
              <button onClick={()=>setMobile(false)}
                className="btn btn-ghost btn-sm"
                style={{borderRadius:0, background: !mobile ? "var(--mv-primary)" : "transparent", color: !mobile ? "#fff":""}}
                title="Desktop">
                <Monitor size={13}/>
              </button>
              <button onClick={()=>setMobile(true)}
                className="btn btn-ghost btn-sm"
                style={{borderRadius:0, background: mobile ? "var(--mv-primary)" : "transparent", color: mobile ? "#fff":""}}
                title="Mobile">
                <Smartphone size={13}/>
              </button>
            </div>
          )}
          <button onClick={()=>setPreview(!preview)}
            className="btn btn-ghost btn-sm"
            data-testid={`${testId}-toggle-preview`}>
            {preview ? <><Code size={13}/> Editor HTML</> : <><Eye size={13}/> Anteprima</>}
          </button>
          <button onClick={sendTest} disabled={testing}
            className="btn btn-primary btn-sm"
            data-testid={`${testId}-send-test`}>
            {testing ? <span className="spinner"/> : <><Send size={13}/> Invia test a me</>}
          </button>
        </div>
      </div>

      <label className="label">Oggetto</label>
      <input className="input"
        value={subject || ""}
        placeholder={subjectPlaceholder}
        onChange={e=>onSubjectChange(e.target.value)}
        data-testid={`${testId}-subject`}/>
      {preview && (
        <div className="text-small text-muted" style={{marginTop:4}}>
          Anteprima oggetto: <strong style={{color:"var(--mv-text)"}}>{previewSubject || "—"}</strong>
        </div>
      )}

      <label className="label mt-4">{preview ? "Anteprima HTML (dati di esempio)" : "Corpo HTML"}</label>
      {preview ? (
        <div style={{
          background:"#525659", borderRadius:8, padding:14,
          display:"flex", justifyContent:"center",
        }}>
          <iframe
            srcDoc={previewHtml}
            title={`${testId}-preview`}
            data-testid={`${testId}-preview-iframe`}
            style={{
              width: iframeWidth, height,
              border:"none", borderRadius:6, background:"white",
              boxShadow:"0 2px 12px rgba(0,0,0,0.2)",
              transition:"width 0.2s",
            }}/>
        </div>
      ) : (
        <div style={{border:"1px solid var(--mv-border)", borderRadius:8, overflow:"hidden"}}
          data-testid={`${testId}-editor`}>
          <Editor
            height={`${height}px`}
            defaultLanguage="html"
            value={html || ""}
            onChange={(v)=>onHtmlChange(v || "")}
            options={{
              minimap:{enabled:false}, fontSize:13, wordWrap:"on",
              lineNumbers:"on", scrollBeyondLastLine:false, automaticLayout:true,
            }}/>
        </div>
      )}
    </div>
  );
}
